import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import type { TextButtonProps } from '@bonniernews/dn-design-system-web/components/button/button-types.ts'
import { TextButton } from '@bonniernews/dn-design-system-web/components/text-button/text-button.tsx'

export interface PageData extends SharedProps {
  /** Button/link label */
  label?: string
  /** Link */
  url?: string
  /** Mark is current */
  current?: boolean
  /** Ex. { target: '_blank', 'data-test': 'lorem ipsum' } */
  attributes?: { [key: string]: string }
}

export interface PaginationProps extends SharedProps {
  /** Array with page objects */
  pages?: PageData[]
  /** Previous button/link label */
  previousLabel?: TextButtonProps['text']
  /** Previous link */
  previousUrl?: TextButtonProps['href']
  previousClassNames?: TextButtonProps['classNames']
  previousAttributes?: TextButtonProps['attributes']
  /** Previous button/link label */
  nextLabel?: TextButtonProps['text']
  /** Previous link */
  nextUrl?: TextButtonProps['href']
  nextClassNames?: TextButtonProps['classNames']
  nextAttributes?: TextButtonProps['attributes']
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/commponents/pagination](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/pagination)
 * - Storybook: [Pagination](https://designsystem.dn.se/?path=/docs/basic-pagination--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/pagination/pagination.scss'`
 */
export const Pagination = ({
  pages,
  previousLabel,
  previousUrl,
  previousClassNames,
  previousAttributes,
  nextLabel,
  nextUrl,
  nextClassNames,
  nextAttributes,
  classNames,
  attributes,
}: PaginationProps) => {
  const componentClassName = 'ds-pagination'
  const classes = formatClassString([componentClassName, !pages ? `${componentClassName}--simple` : '', classNames])
  const isPrevDisabled = !previousUrl
  const prevClasses = formatClassString([
    `${componentClassName}__prev`,
    previousClassNames,
    isPrevDisabled ? 'invisible' : '',
  ])
  const nextClasses = formatClassString([`${componentClassName}__next`, nextClassNames, !nextUrl ? 'invisible' : ''])

  return (
    <nav className={classes} {...attributes}>
      <TextButton
        text={previousLabel || 'Föregående'}
        size='lg'
        hideTextOnMobile={!!pages}
        iconPosition='left'
        iconName='arrow_back'
        classNames={prevClasses}
        attributes={previousAttributes}
        href={previousUrl}
        disabled={isPrevDisabled}
      />
      {pages && (
        <div className={`${componentClassName}__pages`}>
          {pages.map((pageObj) => {
            const page = pageObj as PageData
            const pageClass = componentClassName + '__page'
            const ariaLabelProp = !page.attributes?.['aria-label'] ? { 'aria-label': `sida ${page.label}` } : {}

            return page.current ? (
              <span
                className={`${pageClass} ${pageClass}--current`}
                aria-current='page'
                {...ariaLabelProp}
                {...page.attributes}
              >
                {page.label}
              </span>
            ) : (
              <a href={page.url} className={pageClass} {...ariaLabelProp} {...page.attributes}>
                {page.label}
              </a>
            )
          })}
        </div>
      )}
      <TextButton
        text={nextLabel || 'Nästa'}
        size='lg'
        hideTextOnMobile={!!pages}
        iconPosition='right'
        iconName='arrow_forward'
        classNames={`${componentClassName}__next ${nextClasses || ''}`}
        attributes={nextAttributes}
        href={nextUrl}
        disabled={!nextUrl}
      />
    </nav>
  )
}
